import type { FC, ReactNode } from 'react'
import type { Track } from '@/lib/tracks'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { isTrack } from '@/lib/tracks'

export type RepeatMode = 'off' | 'all' | 'one'

export interface PlayerTime {
    currentTime: number
    duration: number
    buffered: number
}

export interface PlayerApi {
    queue: Array<Track>
    index: number
    current: Track | null
    isPlaying: boolean
    isBuffering: boolean
    volume: number
    muted: boolean
    repeat: RepeatMode
    shuffle: boolean
    error: string | null
    nowPlayingOpen: boolean
    nowPlayingTab: 'playing' | 'queue'
    playTracks: (tracks: Array<Track>, startIndex?: number) => void
    toggleTrack: (track: Track, context?: Array<Track>) => void
    togglePlay: () => void
    play: () => void
    pause: () => void
    next: () => void
    previous: () => void
    seek: (seconds: number) => void
    setVolume: (volume: number) => void
    toggleMute: () => void
    cycleRepeat: () => void
    toggleShuffle: () => void
    enqueue: (track: Track) => void
    playNext: (track: Track) => void
    removeAt: (index: number) => void
    moveTrack: (from: number, to: number) => void
    jumpTo: (index: number) => void
    clearQueue: () => void
    openNowPlaying: (tab?: 'playing' | 'queue') => void
    closeNowPlaying: () => void
    setNowPlayingTab: (tab: 'playing' | 'queue') => void
    enableAnalyser: () => AnalyserNode | null
    getAnalyser: () => AnalyserNode | null
    isCurrent: (id: string) => boolean
}

const STORAGE_KEY = 'serene-player-v1'

const noop = () => undefined

const PlayerContext = createContext<PlayerApi>({
    queue: [],
    index: -1,
    current: null,
    isPlaying: false,
    isBuffering: false,
    volume: 0.85,
    muted: false,
    repeat: 'off',
    shuffle: false,
    error: null,
    nowPlayingOpen: false,
    nowPlayingTab: 'playing',
    playTracks: noop,
    toggleTrack: noop,
    togglePlay: noop,
    play: noop,
    pause: noop,
    next: noop,
    previous: noop,
    seek: noop,
    setVolume: noop,
    toggleMute: noop,
    cycleRepeat: noop,
    toggleShuffle: noop,
    enqueue: noop,
    playNext: noop,
    removeAt: noop,
    moveTrack: noop,
    jumpTo: noop,
    clearQueue: noop,
    openNowPlaying: noop,
    closeNowPlaying: noop,
    setNowPlayingTab: noop,
    enableAnalyser: () => null,
    getAnalyser: () => null,
    isCurrent: () => false
})

const TimeContext = createContext<PlayerTime>({ currentTime: 0, duration: 0, buffered: 0 })

export const usePlayer = (): PlayerApi => useContext(PlayerContext)

export const usePlayerTime = (): PlayerTime => useContext(TimeContext)

const shuffleOrder = (length: number, keep: number): Array<number> => {
    const rest = Array.from({ length }, (_, index) => index).filter(index => index !== keep)

    for (let index = rest.length - 1; index > 0; index -= 1) {
        const swap = Math.floor(Math.random() * (index + 1))
        const value = rest[index]

        rest[index] = rest[swap]
        rest[swap] = value
    }

    return keep >= 0 && keep < length ? [keep, ...rest] : rest
}

const linearOrder = (length: number): Array<number> => Array.from({ length }, (_, index) => index)

interface SavedState {
    queue: Array<Track>
    index: number
    time: number
    volume: number
    muted: boolean
    repeat: RepeatMode
    shuffle: boolean
}

export const PlayerProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [queue, setQueue] = useState<Array<Track>>([])
    const [index, setIndex] = useState(-1)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isBuffering, setIsBuffering] = useState(false)
    const [volume, setVolumeState] = useState(0.85)
    const [muted, setMuted] = useState(false)
    const [repeat, setRepeat] = useState<RepeatMode>('off')
    const [shuffle, setShuffle] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [nowPlayingOpen, setNowPlayingOpen] = useState(false)
    const [nowPlayingTab, setNowPlayingTab] = useState<'playing' | 'queue'>('playing')
    const [time, setTime] = useState<PlayerTime>({ currentTime: 0, duration: 0, buffered: 0 })

    const stateRef = useRef({ queue, index, repeat, shuffle })
    const orderRef = useRef<Array<number>>([])
    const pendingSeekRef = useRef<number | null>(null)
    const retriedRef = useRef<string | null>(null)
    const countedRef = useRef<string | null>(null)
    const restoredRef = useRef(false)
    const contextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const isBufferingRef = useRef(false)

    stateRef.current = { queue, index, repeat, shuffle }
    isBufferingRef.current = isBuffering

    const current = index >= 0 && index < queue.length ? queue[index] : null

    const resumeContext = useCallback(() => {
        const context = contextRef.current

        if (context && context.state === 'suspended') context.resume().catch(() => undefined)
    }, [])

    const startPlayback = useCallback(() => {
        const audio = audioRef.current

        if (!audio) return

        resumeContext()
        setError(null)

        const attempt = audio.play()

        if (attempt && typeof attempt.catch === 'function') {
            attempt.catch((reason: unknown) => {
                const name = reason instanceof Error ? reason.name : ''

                if (name !== 'AbortError') setIsPlaying(false)
            })
        }
    }, [resumeContext])

    const loadTrack = useCallback((track: Track, autoplay: boolean, startAt = 0) => {
        const audio = audioRef.current

        if (!audio) return

        retriedRef.current = null
        pendingSeekRef.current = startAt > 0 ? startAt : null
        setError(null)
        setIsBuffering(autoplay)
        setTime({ currentTime: startAt, duration: track.duration ?? 0, buffered: 0 })

        audio.src = track.src
        audio.load()

        if (autoplay) startPlayback()
    }, [startPlayback])

    const rebuildOrder = useCallback((length: number, keep: number, shuffled: boolean) => {
        orderRef.current = shuffled ? shuffleOrder(length, keep) : linearOrder(length)
    }, [])

    const neighbour = useCallback((direction: 1 | -1, wrap: boolean): number | null => {
        const { queue: list, index: position, repeat: mode, shuffle: shuffled } = stateRef.current

        if (list.length === 0) return null

        if (orderRef.current.length !== list.length) rebuildOrder(list.length, position, shuffled)

        const order = orderRef.current
        const at = Math.max(0, order.indexOf(position))
        let target = at + direction

        if (target >= order.length) {
            if (mode === 'all' || wrap) target = 0
            else return null
        }

        if (target < 0) {
            if (mode === 'all' || wrap) target = order.length - 1
            else return null
        }

        return order[target]
    }, [rebuildOrder])

    const goTo = useCallback((target: number, autoplay: boolean) => {
        const list = stateRef.current.queue
        const track = list[target]

        if (!track) return

        stateRef.current = { ...stateRef.current, index: target }
        setIndex(target)
        loadTrack(track, autoplay)
    }, [loadTrack])

    const playTracks = useCallback((tracks: Array<Track>, startIndex = 0) => {
        const list = tracks.filter(isTrack)

        if (list.length === 0) return

        const start = Math.min(Math.max(0, startIndex), list.length - 1)

        stateRef.current = { ...stateRef.current, queue: list, index: start }
        rebuildOrder(list.length, start, stateRef.current.shuffle)
        setQueue(list)
        setIndex(start)
        loadTrack(list[start], true)
    }, [loadTrack, rebuildOrder])

    const play = useCallback(() => {
        const audio = audioRef.current

        if (!audio || !audio.src) {
            const { queue: list, index: position } = stateRef.current

            if (list[position]) loadTrack(list[position], true)

            return
        }

        startPlayback()
    }, [loadTrack, startPlayback])

    const pause = useCallback(() => {
        audioRef.current?.pause()
    }, [])

    const togglePlay = useCallback(() => {
        const audio = audioRef.current

        if (audio && !audio.paused) pause()
        else play()
    }, [pause, play])

    const toggleTrack = useCallback((track: Track, context?: Array<Track>) => {
        const { queue: list, index: position } = stateRef.current

        if (list[position]?.id === track.id) {
            togglePlay()

            return
        }

        if (context && context.length > 0) {
            const startIndex = context.findIndex(item => item.id === track.id)

            playTracks(context, startIndex >= 0 ? startIndex : 0)

            return
        }

        const existing = list.findIndex(item => item.id === track.id)

        if (existing >= 0) {
            goTo(existing, true)

            return
        }

        const insertAt = position >= 0 ? position + 1 : 0
        const updated = [...list.slice(0, insertAt), track, ...list.slice(insertAt)]

        stateRef.current = { ...stateRef.current, queue: updated }
        rebuildOrder(updated.length, insertAt, stateRef.current.shuffle)
        setQueue(updated)
        goTo(insertAt, true)
    }, [goTo, playTracks, rebuildOrder, togglePlay])

    const next = useCallback(() => {
        const target = neighbour(1, true)

        if (target === null) return

        const audio = audioRef.current

        goTo(target, Boolean(audio && !audio.paused) || stateRef.current.queue.length > 0)
    }, [goTo, neighbour])

    const previous = useCallback(() => {
        const audio = audioRef.current

        if (audio && audio.currentTime > 3) {
            audio.currentTime = 0

            return
        }

        const target = neighbour(-1, false)

        if (target === null) {
            if (audio) audio.currentTime = 0

            return
        }

        goTo(target, true)
    }, [goTo, neighbour])

    const seek = useCallback((seconds: number) => {
        const audio = audioRef.current

        if (!audio || !Number.isFinite(seconds)) return

        const limit = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : seconds

        audio.currentTime = Math.min(Math.max(0, seconds), limit)
        setTime(value => ({ ...value, currentTime: audio.currentTime }))
    }, [])

    const setVolume = useCallback((value: number) => {
        const audio = audioRef.current
        const clamped = Math.min(1, Math.max(0, value))

        setVolumeState(clamped)

        if (audio) {
            audio.volume = clamped
            if (clamped > 0 && audio.muted) audio.muted = false
        }

        if (clamped > 0) setMuted(false)
    }, [])

    const toggleMute = useCallback(() => {
        const audio = audioRef.current

        setMuted(value => {
            if (audio) audio.muted = !value

            return !value
        })
    }, [])

    const cycleRepeat = useCallback(() => {
        setRepeat(mode => (mode === 'off' ? 'all' : mode === 'all' ? 'one' : 'off'))
    }, [])

    const toggleShuffle = useCallback(() => {
        setShuffle(value => {
            const enabled = !value
            const { queue: list, index: position } = stateRef.current

            rebuildOrder(list.length, position, enabled)
            stateRef.current = { ...stateRef.current, shuffle: enabled }

            return enabled
        })
    }, [rebuildOrder])

    const enqueue = useCallback((track: Track) => {
        if (!isTrack(track)) return

        const { queue: list, index: position } = stateRef.current

        if (list.some(item => item.id === track.id)) return

        const updated = [...list, track]

        stateRef.current = { ...stateRef.current, queue: updated }
        rebuildOrder(updated.length, position, stateRef.current.shuffle)
        setQueue(updated)

        if (position < 0) {
            stateRef.current = { ...stateRef.current, index: 0 }
            setIndex(0)
            loadTrack(track, false)
        }
    }, [loadTrack, rebuildOrder])

    const playNext = useCallback((track: Track) => {
        if (!isTrack(track)) return

        const { queue: list, index: position } = stateRef.current
        const without = list.filter(item => item.id !== track.id)
        const currentId = list[position]?.id
        const currentPosition = currentId ? without.findIndex(item => item.id === currentId) : -1
        const insertAt = currentPosition + 1
        const updated = [...without.slice(0, insertAt), track, ...without.slice(insertAt)]

        stateRef.current = { ...stateRef.current, queue: updated, index: currentPosition >= 0 ? currentPosition : 0 }
        rebuildOrder(updated.length, currentPosition, stateRef.current.shuffle)
        setQueue(updated)
        setIndex(currentPosition >= 0 ? currentPosition : 0)

        if (currentPosition < 0) loadTrack(track, false)
    }, [loadTrack, rebuildOrder])

    const removeAt = useCallback((target: number) => {
        const { queue: list, index: position } = stateRef.current

        if (target < 0 || target >= list.length) return

        const updated = list.filter((_, itemIndex) => itemIndex !== target)

        if (updated.length === 0) {
            audioRef.current?.pause()
            audioRef.current?.removeAttribute('src')
            audioRef.current?.load()
            stateRef.current = { ...stateRef.current, queue: [], index: -1 }
            orderRef.current = []
            setQueue([])
            setIndex(-1)
            setIsPlaying(false)
            setNowPlayingOpen(false)

            return
        }

        let nextIndex = position

        if (target < position) nextIndex = position - 1

        if (target === position) {
            nextIndex = Math.min(target, updated.length - 1)

            const audio = audioRef.current
            const wasPlaying = Boolean(audio && !audio.paused)

            stateRef.current = { ...stateRef.current, queue: updated, index: nextIndex }
            rebuildOrder(updated.length, nextIndex, stateRef.current.shuffle)
            setQueue(updated)
            setIndex(nextIndex)
            loadTrack(updated[nextIndex], wasPlaying)

            return
        }

        stateRef.current = { ...stateRef.current, queue: updated, index: nextIndex }
        rebuildOrder(updated.length, nextIndex, stateRef.current.shuffle)
        setQueue(updated)
        setIndex(nextIndex)
    }, [loadTrack, rebuildOrder])

    const moveTrack = useCallback((from: number, to: number) => {
        const { queue: list, index: position } = stateRef.current

        if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return

        const updated = [...list]
        const [moved] = updated.splice(from, 1)

        updated.splice(to, 0, moved)

        const currentId = list[position]?.id
        const nextIndex = currentId ? updated.findIndex(item => item.id === currentId) : position

        stateRef.current = { ...stateRef.current, queue: updated, index: nextIndex }
        rebuildOrder(updated.length, nextIndex, stateRef.current.shuffle)
        setQueue(updated)
        setIndex(nextIndex)
    }, [rebuildOrder])

    const jumpTo = useCallback((target: number) => {
        goTo(target, true)
    }, [goTo])

    const clearQueue = useCallback(() => {
        const audio = audioRef.current

        audio?.pause()
        audio?.removeAttribute('src')
        audio?.load()
        stateRef.current = { ...stateRef.current, queue: [], index: -1 }
        orderRef.current = []
        setQueue([])
        setIndex(-1)
        setIsPlaying(false)
        setNowPlayingOpen(false)
        setTime({ currentTime: 0, duration: 0, buffered: 0 })
    }, [])

    /** Routes the audio through an analyser for the visualiser. Call it from a click so the browser allows it. */
    const enableAnalyser = useCallback((): AnalyserNode | null => {
        if (analyserRef.current) {
            resumeContext()

            return analyserRef.current
        }

        const audio = audioRef.current

        if (!audio || typeof window === 'undefined') return null

        const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

        if (!Context) return null

        try {
            const context = new Context()
            const source = context.createMediaElementSource(audio)
            const analyser = context.createAnalyser()

            analyser.fftSize = 512
            analyser.smoothingTimeConstant = 0.8
            source.connect(analyser)
            analyser.connect(context.destination)

            contextRef.current = context
            analyserRef.current = analyser

            if (context.state === 'suspended') context.resume().catch(() => undefined)

            return analyser
        }
        catch {
            return null
        }
    }, [resumeContext])

    const getAnalyser = useCallback(() => analyserRef.current, [])

    const openNowPlaying = useCallback((tab: 'playing' | 'queue' = 'playing') => {
        enableAnalyser()
        setNowPlayingTab(tab)
        setNowPlayingOpen(true)
    }, [enableAnalyser])

    const closeNowPlaying = useCallback(() => setNowPlayingOpen(false), [])

    const isCurrent = useCallback((id: string) => {
        const { queue: list, index: position } = stateRef.current

        return list[position]?.id === id
    }, [])

    /* Audio element events. */
    useEffect(() => {
        const audio = audioRef.current

        if (!audio) return

        const onPlay = () => {
            setIsPlaying(true)
            resumeContext()
            window.dispatchEvent(new CustomEvent('serene:music-play'))
        }

        const onPause = () => setIsPlaying(false)
        const onWaiting = () => setIsBuffering(true)
        const onPlaying = () => {
            setIsBuffering(false)

            const { queue: list, index: position } = stateRef.current
            const track = list[position]

            if (track && countedRef.current !== track.id) {
                countedRef.current = track.id

                fetch('/api/plays', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: track.id }),
                    keepalive: true
                }).catch(() => undefined)
            }
        }

        const onTime = () => setTime(value => ({ ...value, currentTime: audio.currentTime }))

        const onDuration = () => {
            if (pendingSeekRef.current !== null && Number.isFinite(audio.duration)) {
                audio.currentTime = Math.min(pendingSeekRef.current, Math.max(0, audio.duration - 1))
                pendingSeekRef.current = null
            }

            setTime(value => ({ ...value, duration: Number.isFinite(audio.duration) ? audio.duration : value.duration, currentTime: audio.currentTime }))
        }

        const onProgress = () => {
            try {
                const ranges = audio.buffered

                if (ranges.length > 0) setTime(value => ({ ...value, buffered: ranges.end(ranges.length - 1) }))
            }
            catch {
                return
            }
        }

        const onEnded = () => {
            const { repeat: mode } = stateRef.current

            if (mode === 'one') {
                audio.currentTime = 0
                startPlayback()

                return
            }

            const target = neighbour(1, false)

            if (target === null) {
                setIsPlaying(false)
                audio.currentTime = 0

                return
            }

            countedRef.current = null
            goTo(target, true)
        }

        const onError = () => {
            const { queue: list, index: position } = stateRef.current
            const track = list[position]

            if (!track || !audio.getAttribute('src')) return

            if (retriedRef.current !== track.id) {
                retriedRef.current = track.id

                const resumeAt = audio.currentTime
                const wasPlaying = !audio.paused || isBufferingRef.current

                pendingSeekRef.current = resumeAt > 0 ? resumeAt : null
                audio.src = `${track.src}?retry=${Date.now()}`
                audio.load()

                if (wasPlaying) startPlayback()

                return
            }

            setIsPlaying(false)
            setIsBuffering(false)
            setError(`"${track.title}" could not be played right now.`)
        }

        const onVolume = () => {
            setVolumeState(audio.volume)
            setMuted(audio.muted)
        }

        const onCanPlay = () => setIsBuffering(false)

        audio.addEventListener('play', onPlay)
        audio.addEventListener('pause', onPause)
        audio.addEventListener('waiting', onWaiting)
        audio.addEventListener('playing', onPlaying)
        audio.addEventListener('canplay', onCanPlay)
        audio.addEventListener('timeupdate', onTime)
        audio.addEventListener('durationchange', onDuration)
        audio.addEventListener('loadedmetadata', onDuration)
        audio.addEventListener('progress', onProgress)
        audio.addEventListener('ended', onEnded)
        audio.addEventListener('error', onError)
        audio.addEventListener('volumechange', onVolume)

        return () => {
            audio.removeEventListener('play', onPlay)
            audio.removeEventListener('pause', onPause)
            audio.removeEventListener('waiting', onWaiting)
            audio.removeEventListener('playing', onPlaying)
            audio.removeEventListener('canplay', onCanPlay)
            audio.removeEventListener('timeupdate', onTime)
            audio.removeEventListener('durationchange', onDuration)
            audio.removeEventListener('loadedmetadata', onDuration)
            audio.removeEventListener('progress', onProgress)
            audio.removeEventListener('ended', onEnded)
            audio.removeEventListener('error', onError)
            audio.removeEventListener('volumechange', onVolume)
        }
    }, [goTo, neighbour, resumeContext, startPlayback])

    /* Restore the last session after mount, paused, so nothing ever plays by itself. */
    useEffect(() => {
        if (restoredRef.current) return

        restoredRef.current = true

        try {
            const raw = window.localStorage.getItem(STORAGE_KEY)

            if (!raw) return

            const saved = JSON.parse(raw) as Partial<SavedState>
            const list = Array.isArray(saved.queue) ? saved.queue.filter(isTrack).slice(0, 200) : []
            const savedVolume = typeof saved.volume === 'number' ? Math.min(1, Math.max(0, saved.volume)) : 0.85

            setVolumeState(savedVolume)
            if (audioRef.current) audioRef.current.volume = savedVolume

            if (saved.muted) {
                setMuted(true)
                if (audioRef.current) audioRef.current.muted = true
            }

            if (saved.repeat === 'all' || saved.repeat === 'one') setRepeat(saved.repeat)
            if (saved.shuffle) setShuffle(true)

            if (list.length === 0) return

            const position = typeof saved.index === 'number' && saved.index >= 0 && saved.index < list.length ? saved.index : 0

            stateRef.current = { ...stateRef.current, queue: list, index: position, shuffle: Boolean(saved.shuffle) }
            rebuildOrder(list.length, position, Boolean(saved.shuffle))
            setQueue(list)
            setIndex(position)
            loadTrack(list[position], false, typeof saved.time === 'number' ? saved.time : 0)
        }
        catch {
            window.localStorage.removeItem(STORAGE_KEY)
        }
    }, [loadTrack, rebuildOrder])

    /* Remember the queue and settings in this browser. */
    useEffect(() => {
        if (!restoredRef.current) return

        const save = () => {
            try {
                const state: SavedState = {
                    queue,
                    index,
                    time: audioRef.current?.currentTime ?? 0,
                    volume,
                    muted,
                    repeat,
                    shuffle
                }

                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
            }
            catch {
                return
            }
        }

        const timer = window.setTimeout(save, 400)

        window.addEventListener('pagehide', save)

        return () => {
            window.clearTimeout(timer)
            window.removeEventListener('pagehide', save)
        }
    }, [queue, index, volume, muted, repeat, shuffle, isPlaying])

    /* Pause the music when the hero ambience or a video starts. */
    useEffect(() => {
        const onOtherAudio = () => audioRef.current?.pause()

        window.addEventListener('serene:other-audio', onOtherAudio)

        return () => window.removeEventListener('serene:other-audio', onOtherAudio)
    }, [])

    /* Lock screen and media key controls. */
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return

        const session = navigator.mediaSession

        if (!current) {
            session.metadata = null

            return
        }

        try {
            session.metadata = new MediaMetadata({
                title: current.title,
                artist: 'Serene Corpse',
                album: current.is_demo ? 'Demonstration audio' : (current.subtitle ?? 'Serene Corpse'),
                artwork: current.cover ? [{ src: current.cover, sizes: '512x512' }] : [{ src: '/favicon.svg', sizes: 'any', type: 'image/svg+xml' }]
            })

            session.setActionHandler('play', () => play())
            session.setActionHandler('pause', () => pause())
            session.setActionHandler('previoustrack', () => previous())
            session.setActionHandler('nexttrack', () => next())
            session.setActionHandler('seekto', details => {
                if (typeof details.seekTime === 'number') seek(details.seekTime)
            })
        }
        catch {
            return
        }
    }, [current, next, pause, play, previous, seek])

    const api = useMemo<PlayerApi>(() => ({
        queue,
        index,
        current,
        isPlaying,
        isBuffering,
        volume,
        muted,
        repeat,
        shuffle,
        error,
        nowPlayingOpen,
        nowPlayingTab,
        playTracks,
        toggleTrack,
        togglePlay,
        play,
        pause,
        next,
        previous,
        seek,
        setVolume,
        toggleMute,
        cycleRepeat,
        toggleShuffle,
        enqueue,
        playNext,
        removeAt,
        moveTrack,
        jumpTo,
        clearQueue,
        openNowPlaying,
        closeNowPlaying,
        setNowPlayingTab,
        enableAnalyser,
        getAnalyser,
        isCurrent
    }), [
        queue, index, current, isPlaying, isBuffering, volume, muted, repeat, shuffle, error, nowPlayingOpen, nowPlayingTab,
        playTracks, toggleTrack, togglePlay, play, pause, next, previous, seek, setVolume, toggleMute, cycleRepeat, toggleShuffle,
        enqueue, playNext, removeAt, moveTrack, jumpTo, clearQueue, openNowPlaying, closeNowPlaying, enableAnalyser, getAnalyser, isCurrent
    ])

    return (
        <PlayerContext.Provider value={api}>
            <TimeContext.Provider value={time}>
                {children}

                <audio ref={audioRef} preload='metadata' className='hidden' aria-hidden='true' />
            </TimeContext.Provider>
        </PlayerContext.Provider>
    )
}
